import { Button, Stack, Group, Text, ActionIcon, Alert, Box, Switch, NumberInput, Divider } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { IconPlus, IconTrash, IconAlertCircle } from "@tabler/icons-react";
import { z } from "zod";

import { useUpdateMySession } from "@hooks/mutations/onboarding/useUpdateMySession";
import { useTranslation } from "react-i18next";

const timeBlockSchema = z.object({
  start_time: z.string().min(5, "Start time required"),
  end_time: z.string().min(5, "End time required"),
}).refine(data => {
  if (data.start_time && data.end_time) {
    const start = parseInt(data.start_time.split(':')[0]) * 60 + parseInt(data.start_time.split(':')[1]);
    const end = parseInt(data.end_time.split(':')[0]) * 60 + parseInt(data.end_time.split(':')[1]);
    return end > start;
  }
  return true;
}, {
  message: "End time must be after start time",
  path: ["end_time"]
});

const schema = z.object({
  slot_duration_minutes: z.number().min(5).max(240),
  buffer_time_minutes: z.number().min(0).max(60),
  booking_lead_time_minutes: z.number().min(0),
  daily_schedule: z.record(z.string(), z.array(timeBlockSchema)),
}).refine(
  data => Object.values(data.daily_schedule).some(blocks => blocks.length > 0),
  {
    message: "Add at least one availability period before continuing",
    path: ["daily_schedule"],
  }
);

type Inputs = z.infer<typeof schema>;

interface Step5Props {
  sessionData: any;
}

const DAYS_OF_WEEK = [
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
  { value: "0", label: "Sunday" },
];

export const Step5Availability = ({ sessionData }: Step5Props) => {
  const { t } = useTranslation();
  const { mutate: updateSession, isLoading: isSaving } = useUpdateMySession();

  // Rehydrate initial state from session if it exists
  const initialSchedule: Record<string, any[]> = {};
  DAYS_OF_WEEK.forEach(day => {
    initialSchedule[day.value] = [];
  });
  
  let initialDuration = 30;
  let initialBuffer = 5;
  let initialLeadTime = 0;

  if (sessionData.availability_template) {
    // The backend stores daily_schedule where each block has duration/buffer, we just extract from the first available block
    const dbSchedule = sessionData.availability_template.daily_schedule;
    if (dbSchedule) {
      let foundGlobalConfig = false;
      Object.keys(dbSchedule).forEach(day => {
        initialSchedule[day] = dbSchedule[day].map((block: any) => {
          if (!foundGlobalConfig) {
            initialDuration = block.slot_duration_minutes || 30;
            initialBuffer = block.buffer_time_minutes || 5;
            foundGlobalConfig = true;
          }
          return {
            start_time: block.start_time,
            end_time: block.end_time,
          };
        });
      });
    }
    
    if (sessionData.availability_template.slot_config) {
      initialLeadTime = sessionData.availability_template.slot_config.booking_lead_time_minutes || 0;
    }
  }

  const form = useForm<Inputs>({
    validate: zodResolver(schema),
    initialValues: {
      slot_duration_minutes: initialDuration,
      buffer_time_minutes: initialBuffer,
      booking_lead_time_minutes: initialLeadTime,
      daily_schedule: initialSchedule,
    },
  });

  const compileTemplate = (values: Inputs) => {
    const compiledSchedule: Record<string, any[]> = {};
    const activeDays: number[] = [];

    Object.keys(values.daily_schedule).forEach(dayKey => {
      const blocks = values.daily_schedule[dayKey];
      if (blocks && blocks.length > 0) {
        activeDays.push(parseInt(dayKey));
        compiledSchedule[dayKey] = blocks.map(block => ({
          start_time: block.start_time,
          end_time: block.end_time,
          slot_duration_minutes: values.slot_duration_minutes,
          buffer_time_minutes: values.buffer_time_minutes
        }));
      }
    });

    return {
      type: "weekly",
      daily_schedule: compiledSchedule,
      recurrence: {
        type: "weekly",
        days_of_week: activeDays,
        start_date: new Date().toISOString().split('T')[0], // Today
        end_date: null
      },
      slot_config: {
        quantity: 1,
        default_booked: false,
        booking_lead_time_minutes: values.booking_lead_time_minutes
      },
      generation_horizon_days: 90
    };
  };

  const handleSaveAndContinue = (values: Inputs) => {
    updateSession({
      availability_template: compileTemplate(values),
      current_step: 6, // Advance to next step (Uploads)
    });
  };

  const handleSaveDraft = () => {
    const values = form.getValues();
    updateSession({
      availability_template: compileTemplate(values),
    });
  };

  const handleBack = () => {
    updateSession({ current_step: 4 });
  };

  return (
    <form onSubmit={form.onSubmit(handleSaveAndContinue)}>
      <Stack gap="lg">
        <Alert color="blue" icon={<IconAlertCircle size={16} />}>
          Set up your regular weekly availability. Our system will automatically generate your bookable appointment slots 90 days in advance based on this schedule!
        </Alert>

        <Group grow style={{ background: "#f8f9fa", padding: "16px", borderRadius: "8px", border: "1px solid #e9ecef" }}>
          <NumberInput
            label="Slot Duration (Minutes)"
            description="How long is a standard consultation?"
            placeholder="e.g. 30"
            withAsterisk
            min={5}
            step={5}
            max={240}
            key={form.key("slot_duration_minutes")}
            {...form.getInputProps("slot_duration_minutes")}
          />
          <NumberInput
            label="Buffer Time (Minutes)"
            description="Rest time between patients"
            placeholder="e.g. 5"
            withAsterisk
            min={0}
            step={5}
            max={60}
            key={form.key("buffer_time_minutes")}
            {...form.getInputProps("buffer_time_minutes")}
          />
          <NumberInput
            label="Booking Lead Time"
            description="Min. minutes to book in advance"
            placeholder="e.g. 60"
            withAsterisk
            min={0}
            step={15}
            key={form.key("booking_lead_time_minutes")}
            {...form.getInputProps("booking_lead_time_minutes")}
          />
        </Group>

        <Divider label="Weekly Schedule" labelPosition="center" />

        {form.errors.daily_schedule && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {form.errors.daily_schedule as string}
          </Alert>
        )}

        <Box>
          {DAYS_OF_WEEK.map((day) => {
            const blocks = form.getValues().daily_schedule[day.value];
            const isActive = blocks.length > 0;

            return (
              <Box key={day.value} mb="md" style={{ border: "1px solid #e9ecef", borderRadius: "8px", overflow: "hidden" }}>
                <Group justify="space-between" p="md" style={{ background: isActive ? "#e8f5e9" : "#f8f9fa" }}>
                  <Text fw={600}>{day.label}</Text>
                  <Switch 
                    checked={isActive} 
                    onChange={(event) => {
                      if (event.currentTarget.checked) {
                        form.setFieldValue(`daily_schedule.${day.value}`, [{ start_time: "09:00", end_time: "17:00" }]);
                      } else {
                        form.setFieldValue(`daily_schedule.${day.value}`, []);
                      }
                    }} 
                    size="md" 
                  />
                </Group>
                
                {isActive && (
                  <Stack p="md" gap="xs">
                    {blocks.map((_, index) => (
                      <Group key={index} align="flex-end" grow>
                        <Stack gap={4}>
                          <Text size="sm" fw={500}>Start Time</Text>
                          <input 
                            type="time" 
                            required
                            style={{ padding: "8px 12px", border: "1px solid #ced4da", borderRadius: "4px", fontSize: "14px" }}
                            {...form.getInputProps(`daily_schedule.${day.value}.${index}.start_time`)}
                          />
                        </Stack>
                        <Stack gap={4}>
                          <Text size="sm" fw={500}>End Time</Text>
                          <input 
                            type="time" 
                            required
                            style={{ padding: "8px 12px", border: "1px solid #ced4da", borderRadius: "4px", fontSize: "14px" }}
                            {...form.getInputProps(`daily_schedule.${day.value}.${index}.end_time`)}
                          />
                        </Stack>
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          size="lg"
                          mb={4}
                          onClick={() => form.removeListItem(`daily_schedule.${day.value}`, index)}
                          title="Remove session"
                          style={{ flexGrow: 0, width: "auto" }}
                        >
                          <IconTrash size={20} />
                        </ActionIcon>
                      </Group>
                    ))}
                    
                    {form.errors[`daily_schedule.${day.value}`] && (
                       <Text c="red" size="sm">{form.errors[`daily_schedule.${day.value}`] as string}</Text>
                    )}
                    
                    <Button
                      variant="subtle"
                      leftSection={<IconPlus size={14} />}
                      size="xs"
                      mt="xs"
                      onClick={() => form.insertListItem(`daily_schedule.${day.value}`, { start_time: "12:00", end_time: "13:00" })}
                      style={{ alignSelf: "flex-start" }}
                    >
                      Add Another Session
                    </Button>
                  </Stack>
                )}
              </Box>
            );
          })}
        </Box>

        <Group justify="space-between" mt="xl">
          <Button variant="outline" onClick={handleBack} loading={isSaving} type="button">
            Back
          </Button>
          <Group>
            <Button variant="outline" onClick={handleSaveDraft} loading={isSaving} type="button">
              Save Draft
            </Button>
            <Button type="submit" loading={isSaving} size="md">
              Save & Continue
            </Button>
          </Group>
        </Group>
      </Stack>
    </form>
  );
};
